import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTitle, formatDate, formatTime, formatPrice, formatDuration } from "@/lib/format";
import { PageLoader, StatusBadge, EmptyState, RatingStars, DemoBadge } from "@/components/common";
import { CustomerActions } from "@/components/bookings/BookingActions";

export default function BookingDetail() {
  useTitle("Booking details");
  const { id } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(() => api.get(`/bookings/${id}`).then(({ data }) => setB(data)).catch(() => setError(true)), [id]);
  useEffect(() => { load(); }, [load]);
  if (error) return <div className="mx-auto max-w-xl px-4 py-20"><EmptyState icon={AlertTriangle} title="Booking not found" text="This booking does not exist or you don't have access to it." testId="booking-not-found" /></div>;
  if (!b) return <PageLoader />;
  const isCustomer = b.customer_id === user.id;
  const rows = [["Reference", b.reference], ["Provider", b.provider_name], ["Service", b.service_name], ["Duration", formatDuration(b.duration_minutes)],
    ["Date", formatDate(b.date)], ["Time", `${formatTime(b.start_time)} - ${formatTime(b.end_time)}`], ["Location", b.address || b.location],
    ["Price", `${formatPrice(b.price)} NZD`], ["Contact name", b.customer_name], ["Phone", b.customer_phone], ["Notes", b.notes || "-"]];
  const rr = b.reschedule_request;
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6" data-testid="booking-detail-page">
      <Link to="/bookings" className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-sky-700"><ChevronLeft className="h-4 w-4" />My Bookings</Link>
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-heading text-2xl font-bold text-slate-900">{b.service_name}</h1>
          <div className="flex gap-2"><StatusBadge status={b.status} testId="detail-status" />{b.is_demo && <DemoBadge />}</div>
        </div>
        {rr && (
          <div className="mt-4 rounded-xl bg-sky-50 p-4 text-sm text-sky-800" data-testid="reschedule-info">
            Reschedule request ({rr.status}): {formatDate(rr.date)} at {formatTime(rr.start_time)} {rr.note && `- "${rr.note}"`}
          </div>
        )}
        <dl className="mt-6 grid gap-x-8 sm:grid-cols-2">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-slate-100 py-3 text-sm"><dt className="text-slate-500">{k}</dt><dd className="text-right font-medium text-slate-900">{v}</dd></div>
          ))}
        </dl>
        {b.review && (
          <div className="mt-6 rounded-xl bg-slate-50 p-4" data-testid="detail-review">
            <RatingStars value={b.review.rating} />
            <p className="mt-2 text-sm text-slate-700">{b.review.comment}</p>
          </div>
        )}
        {isCustomer && <div className="mt-6"><CustomerActions booking={b} onDone={load} /></div>}
        <Link to={`/provider/${b.provider_id}`} className="mt-6 inline-block text-sm font-semibold text-sky-700" data-testid="detail-provider-link">View provider profile</Link>
      </div>
    </div>
  );
}
